export function designRemovalUpdates(items, designId) {
  if (!designId) throw new Error('Missing design identifier.');
  return Object.fromEntries(items
    .filter(item => item.properties?._diy_design_id === designId && item.handle?.startsWith('perlavelle-diy-'))
    .map(item => [item.key, 0]));
}

export async function removeCartDesign({ designId, cartUrl, updateUrl, request = fetch }) {
  const before = await request(`${cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store' });
  if (!before.ok) throw new Error('Unable to read cart.');
  const cart = await before.json();
  const updates = designRemovalUpdates(cart.items, designId);
  if (!Object.keys(updates).length) return;
  const response = await request(updateUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates })
  });
  if (!response.ok) throw new Error('Unable to remove design.');
  const result = await response.json();
  if (result.errors || Object.keys(designRemovalUpdates(result.items, designId)).length) {
    throw new Error('Design removal was not completed.');
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('pd-cart-design')) {
  // Dawn uses the same event source for its page and drawer. Refresh the
  // opposite surface so mixed carts cannot retain an old quantity or total.
  let syncRequest;
  const connectCartSync = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, async event => {
      const page = document.querySelector('cart-items');
      if (!page || event.source !== 'cart-items') return;
      const fromDrawer = Boolean(event.cartData?.sections?.['cart-drawer']);
      const peer = fromDrawer ? page : document.querySelector('cart-drawer-items');
      if (!peer?.getSectionsToRender) return;
      const sections = peer.getSectionsToRender().filter(section =>
        fromDrawer ? section.id === 'main-cart-items' || section.id === 'main-cart-footer' : section.id === 'CartDrawer');
      syncRequest?.abort();
      const request = new AbortController();
      syncRequest = request;
      try {
        const url = new URL(routes.cart_url, window.location.origin);
        url.searchParams.set('sections', sections.map(section => section.section).join(','));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
        if (!response.ok) return;
        const html = await response.json();
        if (request.signal.aborted) return;
        for (const section of sections) {
          const root = document.getElementById(section.id);
          const source = new DOMParser().parseFromString(html[section.section] || '', 'text/html').querySelector(section.selector);
          const target = root?.querySelector(section.selector) || root;
          if (source && target) target.innerHTML = source.innerHTML;
        }
        peer.classList.toggle('is-empty', event.cartData.item_count === 0);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('Unable to refresh the other cart view.');
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connectCartSync, { once: true });
  else connectCartSync();
  let removing = false;
  document.addEventListener('submit', event => {
    if (removing) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  customElements.define('pd-cart-design', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.querySelector('[data-remove-design]').addEventListener('click', async () => {
        if (removing) return;
        removing = true;
        document.documentElement.classList.add('pd-cart-removing');
        const button = this.querySelector('[data-remove-design]');
        const label = button.textContent;
        const error = this.querySelector('[role="alert"]');
        const controls = [...document.querySelectorAll('cart-items button, cart-items input, cart-drawer button, cart-drawer input, button[name="checkout"]')]
          .filter(control => !control.disabled);
        controls.forEach(control => { control.disabled = true; });
        error.hidden = true;
        button.textContent = this.dataset.removing;
        this.setAttribute('aria-busy', 'true');
        try {
          await removeCartDesign({ designId: this.dataset.designId, cartUrl: this.dataset.cartUrl, updateUrl: this.dataset.updateUrl });
          window.location.reload();
        } catch {
          removing = false;
          document.documentElement.classList.remove('pd-cart-removing');
          controls.forEach(control => { control.disabled = false; });
          button.textContent = label;
          error.textContent = this.dataset.error;
          error.hidden = false;
          this.removeAttribute('aria-busy');
        }
      });
    }
  });
}
